import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Reservation } from "@/models/storeModel";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { CalendarIcon } from "lucide-react";

interface EditReservationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reservation: Reservation;
}

type ReservationStatus = "confirmed" | "cancelled" | "completed";

export function EditReservationDialog({
  open,
  onOpenChange,
  reservation,
}: EditReservationDialogProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [formData, setFormData] = useState<null | {
    customer_name: string;
    customer_email: string;
    customer_phone: string;
    party_size: number;
    reservation_date: Date;
    reservation_time: string;
    special_requests: string;
    status: ReservationStatus;
  }>(null);
  useEffect(() => {
    if (open && reservation) {
      setFormData({
        customer_name: reservation.customer_name,
        customer_email: reservation.customer_email,
        customer_phone: reservation.customer_phone || "",
        party_size: reservation.party_size,
        reservation_date: parseISO(reservation.reservation_date),
        reservation_time: reservation.reservation_time,
        special_requests: reservation.special_requests || "",
        status: reservation.status as ReservationStatus,
      });
    }
  }, [open, reservation]);

  if (!formData) return null;

  const timeSlots = Array.from({ length: 24 * 2 }).map((_, i) => {
    const hour = Math.floor(i / 2);
    const minute = i % 2 === 0 ? "00" : "30";
    return `${hour.toString().padStart(2, "0")}:${minute}`;
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data, error } = await supabase
        .from("reservations")
        .update({
          customer_name: formData.customer_name,
          customer_email: formData.customer_email,
          customer_phone: formData.customer_phone,
          party_size: formData.party_size,
          reservation_date: format(formData.reservation_date, "yyyy-MM-dd"),
          reservation_time: formData.reservation_time,
          special_requests: formData.special_requests,
          status: formData.status,
          updated_at: new Date().toISOString(),
        })
        .eq("id", reservation.id);

      if (error) throw error;

      toast({
        title: t("reservations.editSuccess"),
        description: t("reservations.editSuccessDescription"),
      });

      onOpenChange(false);
    } catch (error) {
      console.error("Error updating reservation:", error);
      toast({
        title: t("reservations.editError"),
        description: t("reservations.editErrorDescription"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("reservations.editReservation")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="customer_name">
                {t("reservations.customerName")}
              </Label>
              <Input
                id="customer_name"
                value={formData.customer_name}
                onChange={(e) =>
                  setFormData({ ...formData, customer_name: e.target.value })
                }
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="customer_email">
                {t("reservations.customerEmail")}
              </Label>
              <Input
                id="customer_email"
                type="email"
                value={formData.customer_email}
                onChange={(e) =>
                  setFormData({ ...formData, customer_email: e.target.value })
                }
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="customer_phone">
                {t("reservations.customerPhone")}
              </Label>
              <Input
                id="customer_phone"
                value={formData.customer_phone}
                onChange={(e) =>
                  setFormData({ ...formData, customer_phone: e.target.value })
                }
              />
            </div>

            <div className="grid gap-2">
              <Label>{t("reservations.date")}</Label>
              <div className="space-y-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCalendar(!showCalendar)}
                  className="w-full justify-start text-left font-normal"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(formData.reservation_date, "PPP")}
                </Button>
                {showCalendar && (
                  <Calendar
                    mode="single"
                    selected={formData.reservation_date}
                    onSelect={(date) =>
                      date &&
                      setFormData({ ...formData, reservation_date: date })
                    }
                    className="rounded-md border"
                  />
                )}
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="reservation_time">{t("reservations.time")}</Label>
              <Select
                value={formData.reservation_time}
                onValueChange={(value) =>
                  setFormData({ ...formData, reservation_time: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("reservations.selectTime")} />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((time) => (
                    <SelectItem key={time} value={time}>
                      {time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="party_size">{t("reservations.partySize")}</Label>
              <Input
                id="party_size"
                type="number"
                min="1"
                value={formData.party_size}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    party_size: parseInt(e.target.value),
                  })
                }
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="status">{t("reservations.status")}</Label>
              <Select
                value={formData.status}
                onValueChange={(value: ReservationStatus) =>
                  setFormData({ ...formData, status: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmed">
                    {t("reservations.statuses.confirmed")}
                  </SelectItem>
                  <SelectItem value="cancelled">
                    {t("reservations.statuses.cancelled")}
                  </SelectItem>
                  <SelectItem value="completed">
                    {t("reservations.statuses.completed")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="special_requests">
                {t("reservations.specialRequests")}
              </Label>
              <Textarea
                id="special_requests"
                value={formData.special_requests}
                onChange={(e) =>
                  setFormData({ ...formData, special_requests: e.target.value })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
